import { isNative } from "./platform";

const TOKEN_KEY = "xynex_money_token";
const USER_KEY = "xynex_money_user";

export interface AuthUser {
  id: string;
  email: string;
  tenantId: string | null;
  role: "owner" | "member" | "superadmin";
}

// In-memory cache keeps getToken() synchronous even on native (where the
// backing secure store — Android Keystore via @aparajita/capacitor-secure-storage
// — is async). loadTokenAtBoot() hydrates it once before the app makes its
// first API call (see main.tsx wiring, Task 4). On web the cache mirrors
// localStorage so the browser/PWA behaviour is byte-for-byte unchanged.
let tokenCache: string | null = null;
let userCache: AuthUser | null = null;

async function secure() {
  // Lazy import so the web bundle never eagerly pulls native code at load.
  const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
  return SecureStorage;
}

export async function loadTokenAtBoot(): Promise<void> {
  if (!isNative()) {
    tokenCache = localStorage.getItem(TOKEN_KEY);
    const rawU = localStorage.getItem(USER_KEY);
    userCache = rawU ? safeParse(rawU) : null;
    return;
  }
  try {
    const s = await secure();
    const t = await s.get(TOKEN_KEY).catch(() => null);
    tokenCache = typeof t === "string" ? t : null;
    const u = await s.get(USER_KEY).catch(() => null);
    userCache = typeof u === "string" ? safeParse(u) : null;
  } catch {
    tokenCache = null;
    userCache = null;
  }
}

export function getToken(): string | null {
  return tokenCache;
}

export function setToken(token: string): void {
  tokenCache = token;
  if (!isNative()) {
    localStorage.setItem(TOKEN_KEY, token);
    return;
  }
  void secure()
    .then((s) => s.set(TOKEN_KEY, token))
    .catch(() => {});
}

export function clearToken(): void {
  tokenCache = null;
  userCache = null;
  if (!isNative()) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return;
  }
  void secure()
    .then((s) =>
      Promise.all([
        s.remove(TOKEN_KEY).catch(() => {}),
        s.remove(USER_KEY).catch(() => {}),
      ])
    )
    .catch(() => {});
}

export function getUser(): AuthUser | null {
  return userCache;
}

export function setUser(user: AuthUser): void {
  userCache = user;
  const raw = JSON.stringify(user);
  if (!isNative()) {
    localStorage.setItem(USER_KEY, raw);
    return;
  }
  void secure()
    .then((s) => s.set(USER_KEY, raw))
    .catch(() => {});
}

function safeParse(raw: string): AuthUser | null {
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}
